import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MessageSquare, Plus, Calendar, FileCode } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useActiveThemeId } from '@/hooks/useActiveThemeId';

interface ThemeBuild {
  id: number;
  theme_id: number;
  site_id: number;
  status: string;
  created_at: string;
  updated_at: string;
}

interface ConversationListItem extends ThemeBuild {
  messageCount?: number;
  fileCount?: number;
  lastMessage?: string;
}

const DevEditorConversations: React.FC = () => {
  const { siteSlug } = useParams<{ siteSlug: string }>();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<ConversationListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { themeId, isLoading: themeLoading } = useActiveThemeId(siteSlug);

  useEffect(() => {
    if (!siteSlug || !themeId) return;
    loadConversations();
  }, [siteSlug, themeId]);

  const loadConversations = async () => {
    if (!siteSlug || !themeId) return;
    try {
      setLoading(true);
      const response = await fetch(
        `/sites/${siteSlug}/themes/${themeId}/theme_builds`,
        {
          headers: {
            'Content-Type': 'application/json',
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.theme_builds) {
          const normalized = data.theme_builds.map((build: any) => ({
            ...build,
            messageCount: build.messageCount ?? build.message_count ?? 0,
            fileCount: build.fileCount ?? build.file_count ?? 0,
            lastMessage: build.lastMessage ?? build.last_message ?? undefined,
          }))
          setConversations(normalized);
        }
      }
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const createNewConversation = async () => {
    if (!siteSlug || !themeId) return;
    try {
      const response = await fetch(
        `/sites/${siteSlug}/themes/${themeId}/theme_builds`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.theme_build) {
          // Navigate to the new conversation
          navigate(`/sites/${siteSlug}/devEditor/${data.theme_build.id}`);
        }
      }
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
  };

  const openConversation = (buildId: number) => {
    navigate(`/sites/${siteSlug}/devEditor/${buildId}`);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold">AI Code Conversations</h1>
          <p className="text-muted-foreground mt-1">
            Manage your AI-assisted development sessions
          </p>
        </div>
        <Button onClick={createNewConversation} size="lg">
          <Plus className="mr-2 h-5 w-5" />
          New Conversation
        </Button>
      </div>

      {loading || themeLoading ? (
        <div className="flex items-center justify-center h-64">
          <div className="text-muted-foreground">Loading conversations...</div>
        </div>
      ) : conversations.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <MessageSquare className="h-16 w-16 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No conversations yet</h3>
            <p className="text-muted-foreground text-center mb-4 max-w-md">
              Start a new conversation with the AI assistant to build your project
            </p>
            <Button onClick={createNewConversation}>
              <Plus className="mr-2 h-4 w-4" />
              Create Your First Conversation
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {conversations.map((conversation) => (
            <Card
              key={conversation.id}
              className={cn(
                "cursor-pointer hover:border-primary transition-colors",
                conversation.status === 'active' && "border-primary/50"
              )}
              onClick={() => openConversation(conversation.id)}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-base">
                      Conversation #{conversation.id}
                    </CardTitle>
                    <CardDescription className="flex items-center gap-1 mt-1">
                      <Calendar className="h-3 w-3" />
                      {formatDate(conversation.created_at)}
                    </CardDescription>
                  </div>
                  <div
                    className={cn(
                      "px-2 py-1 rounded-full text-xs font-medium",
                      conversation.status === 'active' && "bg-green-100 text-green-700",
                      conversation.status === 'archived' && "bg-gray-100 text-gray-700",
                      conversation.status === 'completed' && "bg-blue-100 text-blue-700"
                    )}
                  >
                    {conversation.status}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    <span>Messages: {conversation.messageCount || 0}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FileCode className="h-4 w-4" />
                    <span>Files: {conversation.fileCount || 0}</span>
                  </div>
                </div>
                {conversation.lastMessage && (
                  <p className="mt-3 text-sm line-clamp-2 text-muted-foreground">
                    {conversation.lastMessage}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default DevEditorConversations;
